import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import { registerSW } from 'virtual:pwa-register'
import posthog from 'posthog-js'
import { PostHogProvider } from '@posthog/react'
import { GoogleOAuthProvider } from '@react-oauth/google'

const GOOGLE_CLIENT_ID = (import.meta.env.VITE_GOOGLE_CLIENT_ID as string) || ''

registerSW({ immediate: true })

// A new service worker (from a fresh deploy) can take control of this tab
// while the currently-running JS bundle still references the *old* build's
// hashed chunk filenames. Those chunks are gone from both the server and the
// SW's precache, so any later `import()` (e.g. a lazy-loaded route) 404s and
// throws "Failed to fetch dynamically imported module". Reload once when a
// new SW activates so the tab always runs code matching what's cached.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (sessionStorage.getItem('sw-reloaded')) return
    sessionStorage.setItem('sw-reloaded', '1')
    window.location.reload()
  })
}

// Fallback safety net for the same class of failure when it isn't caused by
// a SW controller change (e.g. SW disabled, or a CDN evicted an old chunk
// mid-session): force a one-time reload instead of leaving the app crashed.
window.addEventListener('vite:preloadError', () => {
  if (sessionStorage.getItem('chunk-reloaded')) return
  sessionStorage.setItem('chunk-reloaded', '1')
  window.location.reload()
})
window.addEventListener('unhandledrejection', (event) => {
  const message = String(event.reason?.message || event.reason || '')
  if (!/Failed to fetch dynamically imported module|error loading dynamically imported module/i.test(message)) return
  if (sessionStorage.getItem('chunk-reloaded')) return
  sessionStorage.setItem('chunk-reloaded', '1')
  window.location.reload()
})

const rootElement = document.getElementById('root')

if (!rootElement) {
  throw new Error('Root element not found. Make sure index.html has <div id="root"></div>')
}

// Initialize PostHog (reads Vite env vars from .env.local)
// Never initialize on localhost: the token in .env/.env.local points at the
// shared production project, so local dev and manual QA would otherwise mix
// synthetic traffic into real product analytics.
const _posthog_token = (import.meta.env.VITE_POSTHOG_PROJECT_TOKEN as string) || ''
const _posthog_host = (import.meta.env.VITE_POSTHOG_HOST as string) || 'https://app.posthog.com'
const _posthog_is_local = (() => {
  const h = window.location.hostname
  return /(^|\.)localhost$/.test(h) || h === '127.0.0.1' || h === '0.0.0.0' || h === '::1' || h === '[::1]'
})()
if (_posthog_is_local) {
  // eslint-disable-next-line no-console
  console.info('[PostHog] disabled on localhost')
} else if (_posthog_token && _posthog_token.length > 0) {
  try {
    posthog.init(_posthog_token, { api_host: _posthog_host })
    posthog.register({ app: 'legacy-frontend' })
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('PostHog init failed', e)
  }
} else {
  // eslint-disable-next-line no-console
  console.warn('[PostHog] no project token found; skipping initialization. Ensure VITE_POSTHOG_PROJECT_TOKEN is set and restart the dev server.')
}

// Global instrumentation: capture button clicks and API calls
function installGlobalInstrumentation() {
  // capture clicks on <button> elements or elements with role="button"
  document.addEventListener('click', (ev) => {
    try {
      let el = ev.target as HTMLElement | null
      while (el && el !== document.documentElement) {
        const isButton = el.tagName === 'BUTTON' || el.getAttribute('role') === 'button'
        if (isButton) {
          const props = {
            text: (el.textContent || '').trim().slice(0, 200),
            id: el.id || undefined,
            classes: el.className || undefined,
            dataset: Object.keys(el.dataset).length ? el.dataset : undefined,
          }
          try { posthog.capture('button_clicked', props) } catch (e) {}
          return
        }
        el = el.parentElement
      }
    } catch (e) {
      // ignore
    }
  }, true)

  // instrument fetch
  if (window.fetch) {
    const originalFetch = window.fetch.bind(window)
    // @ts-ignore
    window.fetch = async (input: RequestInfo, init?: RequestInit) => {
      const url = typeof input === 'string' ? input : (input as Request).url
      const method = init?.method || (typeof input === 'object' && 'method' in (input as Request) ? (input as Request).method : 'GET')
      try {
        const res = await originalFetch(input, init)
        try { posthog.capture('api_called', { url, method, status: res.status }) } catch (e) {}
        return res
      } catch (err) {
        try { posthog.capture('api_error', { url, method, error: String(err) }) } catch (e) {}
        throw err
      }
    }
  }

  // instrument XMLHttpRequest
  const OriginalXhr = window.XMLHttpRequest
  // @ts-ignore
  function XhrProxy(this: any) {
    const xhr = new OriginalXhr()
    let url: string | undefined
    let method: string | undefined
    const open = xhr.open
    xhr.open = function (m: string, u: string) {
      method = m
      url = u
      // @ts-ignore
      return open.apply(xhr, arguments)
    }
    const send = xhr.send
    xhr.send = function () {
      xhr.addEventListener('loadend', function () {
        try { posthog.capture('api_called', { url, method, status: xhr.status }) } catch (e) {}
      })
      // @ts-ignore
      return send.apply(xhr, arguments)
    }
    return xhr
  }
  // @ts-ignore
  window.XMLHttpRequest = XhrProxy as any
}

installGlobalInstrumentation()

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <PostHogProvider client={posthog}>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <App />
      </GoogleOAuthProvider>
    </PostHogProvider>
  </React.StrictMode>,
)

